import { useState } from 'react';
import clsx from 'clsx';
import { canReviewPill, isDuplicatePill, duplicatePillFromSendError, canConfirmHoldVerdict } from '../lib/trs-pills.mjs';

// Shared TRS (Trip Request Solver) verdict card + its presentational helpers.
//
// Extracted from pages/TrsVerdicts.jsx so the SAME card renders on two surfaces with zero visual
// divergence: the /trs aggregate review list AND the inline "Trip Request Solver (TRS)" panel on the
// trip-detail page (pages/TripDetail.jsx). A verdict object is one shaped cr_crew_solver_log row
// (server: lib/trs-verdicts.js shapeVerdicts).

// Verdict → badge classes. Six distinct tones — StatusBadge only exposes 5 (green/red/amber/gray/
// teal), so this uses its own inline map to give RECHECK / STALE / ERROR each a distinct color.
export const VERDICT_TONE = {
  SEND:          'bg-emerald-100 text-emerald-800',
  HOLD:          'bg-amber-100 text-amber-800',
  ALREADY_LEGAL: 'bg-tint-fill text-tint',
  RECHECK:       'bg-indigo-100 text-indigo-800',
  STALE:         'bg-slate-200 text-slate-600',
  ERROR:         'bg-rose-100 text-rose-800',
};
export const VERDICT_SYMBOL = {
  SEND: '✅', HOLD: '⏸', ALREADY_LEGAL: '✔', RECHECK: '🔁', STALE: '🕒', ERROR: '⚠',
};
// Order the header summary + filter chips read in — mirrors the server's VERDICT_ORDER.
export const VERDICT_ORDER = ['SEND', 'HOLD', 'ALREADY_LEGAL', 'RECHECK', 'STALE', 'ERROR'];

export function VerdictBadge({ verdict }) {
  const tone = VERDICT_TONE[verdict] || 'bg-slate-200 text-slate-700';
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold', tone)}>
      <span>{VERDICT_SYMBOL[verdict] || '•'}</span>
      <span>{verdict}</span>
    </span>
  );
}

// Reviewer disposition pill (server: lib/trs-verdicts.computeDisposition). Shows where a verdict
// sits in the review loop — Pending until a reviewer Approves/Declines it.
const DISPOSITION_TONE = {
  'Pending':         'bg-slate-100 text-slate-500 border-slate-200',
  'Approved → Sent': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Approved (held)': 'bg-tint-fill text-tint border-separator',
  'Approved (not sent)': 'bg-tint-fill text-tint border-separator',
  'Send failed':     'bg-rose-100 text-rose-800 border-rose-300',
  'Declined':        'bg-rose-50 text-rose-700 border-rose-200',
  'Verdict-changed': 'bg-amber-100 text-amber-800 border-amber-300',
  'Auto-resolved':   'bg-slate-100 text-slate-600 border-slate-300',
};
export function DispositionPill({ disposition }) {
  if (!disposition) return null;
  const pill = disposition.pill || 'Pending';
  const tone = isDuplicatePill(pill)
    ? 'bg-slate-100 text-slate-600 border-slate-300'   // SQP-297: neutral, not an error and not pending
    : (DISPOSITION_TONE[pill] || 'bg-slate-100 text-slate-600 border-slate-200');
  const title = [
    disposition.reviewer && `by ${disposition.reviewer}`,
    disposition.reviewed_at,
    disposition.auto_reason,
    disposition.corrected_verdict && `→ ${disposition.corrected_verdict}`,
    disposition.note && `note: ${disposition.note}`,
    disposition.send_error && (isDuplicatePill(pill) ? `Refused: already delivered — ${disposition.send_error}` : `send error: ${disposition.send_error}`),
    disposition.stale && `(reviewed as ${disposition.verdict_reviewed})`,
  ].filter(Boolean).join(' · ');
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border', tone)} title={title || undefined}>
      {pill}
    </span>
  );
}

// The Sentinel quote id + a deep link to its operator review page (null → plain Q#, no GUID captured).
// Matches the SentinelQuote treatment on the Centralized Requests page.
function QuoteLink({ qid, url }) {
  if (qid == null || qid === '') return <span className="text-slate-400">—</span>;
  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this quote in Sentinel to review / assess"
      className="font-mono text-tint hover:underline"
    >
      Q#{qid}<span className="text-[10px] ml-0.5">↗</span>
    </a>
  ) : (
    <span className="font-mono text-slate-700">Q#{qid}</span>
  );
}

// One-line human reasoning for a verdict, derived from the same fields the solver logged. The trace
// tells us WHY a HOLD held (a near-limit ORANGE candidate vs no candidate at all), so we read it.
function reasoningFor(v) {
  // A candidate is GENUINELY near-limit only when it cleared every RED and is left with orange alone.
  // `anyOrange && !sendable` is NOT that: a candidate still carrying a RED chip almost always ALSO
  // carries an orange chip on some other leg, so the old test fired on plainly-red candidates and the
  // card claimed "the best candidate is near-limit (orange)" when nothing had come close.
  // Q#12125 (sprintbach, CR-1070745, 2026-07-15): ALL 15 candidates were anyRed=true — not one reached
  // orange-only — yet the card reported a near-limit best candidate. Yves flagged it as impossible for
  // that trip (KBOI/PHNL), and he was right. Requiring !anyRed makes the copy mean what it says.
  const traceHasOrange = Array.isArray(v.trace)
    && v.trace.some(c => c && c.anyOrange && !c.anyRed && !c.sendable);
  switch (v.verdict) {
    case 'SEND':
      return v.arrangement
        ? `Legal via ${v.arrangement} — a re-slot with no RED (and no near-limit orange) chip.`
        : 'A legal, all-clean arrangement was found.';
    case 'HOLD': {
      // HOLD reasons split by whether the BASE preview carried a RED chip.
      //  - no RED → the solver only re-slots positioning to clear a RED chip, so nothing was
      //    solvable here; the ACTUAL cause is the Sentinel crew warning that flagged the quote —
      //    NAME it (Crew Duty Day Exceed / Crew Flight Time Near / …). Keying only off !anyRed used
      //    to hardcode "near-limit ORANGE chip / orange-holds policy" even when there was NO orange
      //    and the real cause was a duty-day exceedance (CR-1072948 / Q#12516: base_any_red=0, no
      //    orange in the read, warnings = Crew Duty Day Exceed ×3).
      //  - RED → candidates WERE tried; distinguish "best candidate still near-limit orange" from
      //    "nothing cleared the red".
      const warns = Array.isArray(v.warnings)
        ? [...new Set(v.warnings.map((w) => String(w || '').trim()).filter(Boolean))]
        : [];
      if (!v.base?.anyRed) {
        if (warns.length)
          return `Held on a Sentinel crew warning — ${warns.join(', ')} — with no RED flight-time chip in the base preview. The solver only re-slots positioning to clear a RED chip, so a warning like this is held for human + operator review.`;
        return 'Held on a near-limit (ORANGE) flight-time chip — no RED. The solver only re-slots to clear a RED chip, so a near-limit quote is held for human + operator review (orange-holds policy).';
      }
      return traceHasOrange
        ? 'No fully-clean arrangement — the best candidate is near-limit (orange) rest/duty. Held for human + operator review.'
        : 'No positioning arrangement clears the RED chip. Held for human + operator review.';
    }
    case 'ALREADY_LEGAL': {
      // SQP-069's sibling, found 2026-08-10 on Q#14129/NPDVFK. This used to be the fixed sentence
      // "…and the quote was NOT crew-flagged — genuinely legal", printed on a quote that WAS
      // crew-flagged (flag=1, "Crew warning on primary quote", Sentinel Validate reporting
      // "Near Crew Flight Time Limits"). The verdict was right; the sentence said the opposite.
      // ALREADY_LEGAL is reachable by several routes — being un-flagged is only one — so the card
      // now names the one that actually applied, derived server-side from the same row.
      const al = v.alreadyLegal;
      if (!al) return 'Base preview read fully clean — legal as created. Basis not recorded for this verdict.';
      const limit = al.limit ? ` (${al.limit})` : '';
      switch (al.basis) {
        case 'crew_warned_near_limit_view_confirmed':
          return `Crew-flagged but NEAR the limit, not over${limit} — the crew-chip view was confirmed on and the schedule read clean, so it is legal as created.`;
        case 'crew_warned_near_limit':
          return `Crew-flagged but NEAR the limit, not over${limit} — the schedule read clean, so it is legal as created. The crew-chip view was not recorded for this run.`;
        case 'clean_view_confirmed':
          return 'Base preview read fully clean with the crew-chip view confirmed on, and no crew warning — genuinely legal.';
        default:
          return 'Base preview read fully clean and no crew warning — legal as created. The crew-chip view was not recorded for this run.';
      }
    }
    case 'RECHECK': {
      // SQP-069 — this used to be ONE fixed sentence blaming a view-off / read race for every
      // RECHECK. Measured against prod 2026-08-07 it was wrong on 36 of 36 latest RECHECK rows (31
      // are the Exceed guard; NOT ONE had view_confirmed=0), and it sent two investigations chasing
      // a render race that never happened (Q#13968, Q#13951). The cause now comes from the row —
      // server-side `describeRecheck` (lib/trs-verdicts.js), which re-runs the SAME
      // crewExceedBlocksSend the verdict used, so this copy cannot drift from the decision.
      const rc = v.recheck;
      // No derived cause (pre-037 DB, or an older payload) — say so rather than inventing one.
      if (!rc) return 'A crew-flagged quote that read fully clean, so the read was not trusted. Cause not recorded for this verdict. Unverified, never sent.';
      const tail = ' Unverified, never sent.';
      switch (rc.cause) {
        case 'exceed_contradicts_clean_read': {
          const limit = rc.limit || 'a crew limit exceeded';
          // WHICH input decided is most of the diagnosis. A fresh Validate is authoritative; the
          // creation-time snapshot may predate a crew swap / re-slot that already fixed the quote
          // (SQP-044 — held quotes are never re-validated), so name it as such rather than letting
          // a stale array read as Sentinel's current opinion.
          const src = rc.decidedBy === 'live_validation'
            ? `Sentinel's own Validate reports ${limit}`
            : `The crew warnings captured when this quote was created (no fresh Validate on file) report ${limit}`;
          return `${src} while the schedule read clean — refusing to trust the clean read.${tail}`;
        }
        case 'view_confirmed_off':
          return `The crew-chip view ("Block Time, 24h Limits") was confirmed OFF, so an all-clean read cannot be trusted on a crew-flagged quote.${tail}`;
        case 'view_not_recorded':
          // ⚠️ Deliberately NOT the view-off narrative. view_confirmed is tri-state and NULL means
          // the solver never reported (migration 037 is explicit that collapsing NULL→0 manufactures
          // "the view was off"). 5 of the 36 prod rows are exactly this. See SQP-068(a).
          return `The solver could not confirm the crew-chip view was on, so the clean read was not trusted — and it did not record whether the view was actually off.${tail}`;
        default:
          return `A crew-flagged quote that read fully clean. Sentinel reports no exceeded limit and the crew-chip view was confirmed on, so this row does not explain the recheck.${tail}`;
      }
    }
    case 'STALE':
      return 'Sentinel rejected the quote as no longer valid (past-departure or superseded). Never sent.';
    case 'ERROR':
      return v.error ? `Solver error: ${v.error}` : 'Solver error (see trace).';
    default:
      return '';
  }
}

function BoolCell({ value, trueTone = 'text-rose-700', falseTone = 'text-slate-400' }) {
  return <span className={value ? trueTone : falseTone}>{value ? 'yes' : 'no'}</span>;
}

// Task B1 — P2 Decline. Server: POST /api/trs/verdicts/:operatorQuoteId/decline
// (dashboard/server/routes/trs.js → lib/trs-review.js declineVerdict). Options mirror the server's
// WRONG_CATEGORIES / CORRECTED_VERDICTS enums (kept as a small local list — no shared import across
// the client/server boundary).
const WRONG_CATEGORY_OPTIONS = ['should_send', 'should_hold', 'bad_arrangement', 'misread_crew_data', 'rule_threshold', 'other'];
const CORRECTED_VERDICT_OPTIONS = ['SEND', 'HOLD', 'unknown'];

// Inline decline form — "TRS got this wrong." Captures the structured correction (wrong_category +
// corrected_verdict + a required free-text note) that becomes the learning signal. A Decline NEVER
// sends anything; it only writes a review row + audit row (server-side, see trs-review.js).
// submitting / setSubmitting / error / setError are LIFTED to the parent VerdictCard so the parent
// can (a) disable the Approve button while a decline is in flight and (b) clear a stale decline
// error when the reviewer switches to Approve — enforcing "only one review action in flight at a
// time" (Task B2 code-review fix 1/3).
function DeclineVerdictForm({ operatorQuoteId, onCancel, onDeclined, submitting, setSubmitting, error, setError }) {
  const [wrongCategory, setWrongCategory] = useState(WRONG_CATEGORY_OPTIONS[0]);
  const [correctedVerdict, setCorrectedVerdict] = useState('unknown');
  const [note, setNote] = useState('');

  const noteTrimmed = note.trim();
  const canSubmit = noteTrimmed.length > 0 && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/trs/verdicts/${encodeURIComponent(operatorQuoteId)}/decline`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wrong_category: wrongCategory, corrected_verdict: correctedVerdict, note: noteTrimmed }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.ok) {
        onDeclined({ wrongCategory, correctedVerdict, note: noteTrimmed });
      } else {
        setError(json.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 p-2 border border-rose-200 rounded-md bg-rose-50/40 space-y-2">
      <div className="flex flex-wrap gap-3">
        <label className="text-xs text-slate-600 flex flex-col gap-0.5">
          Wrong because
          <select
            value={wrongCategory}
            onChange={e => setWrongCategory(e.target.value)}
            disabled={submitting}
            className="text-xs border-slate-300 rounded-md px-1.5 py-1"
          >
            {WRONG_CATEGORY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 flex flex-col gap-0.5">
          Should have been
          <select
            value={correctedVerdict}
            onChange={e => setCorrectedVerdict(e.target.value)}
            disabled={submitting}
            className="text-xs border-slate-300 rounded-md px-1.5 py-1"
          >
            {CORRECTED_VERDICT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </label>
      </div>
      <label className="text-xs text-slate-600 flex flex-col gap-0.5">
        Note (required)
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          disabled={submitting}
          rows={2}
          placeholder="What did the solver get wrong?"
          className="text-xs border-slate-300 rounded-md px-1.5 py-1 w-full"
        />
      </label>
      {error && <div className="text-xs text-rose-700">{error}</div>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className={clsx(
            'px-2 py-1 rounded-md text-xs font-medium',
            canSubmit ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          )}
        >
          {submitting ? 'Submitting…' : 'Submit decline'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-2 py-1 rounded-md text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// Task B2 — P3 Approve-HOLD ("TRS correctly held this; confirm and close it out"). NEVER sends —
// server: POST /api/trs/verdicts/:operatorQuoteId/approve (routes/trs.js → trs-review.js
// approveVerdict). Approve-on-SEND (an actual send) is a later task (B5); the server 501s for a
// SEND/ALREADY_LEGAL verdict, but this button is only offered for HOLD/RECHECK (canConfirmHoldVerdict).
// intent:'hold' + expectedLogId make the server refuse (409) rather than SEND if the latest verdict
// has since become SEND-type or is no longer the row this card was rendered from.
async function postApproveHold(operatorQuoteId, expectedLogId) {
  const res = await fetch(`/api/trs/verdicts/${encodeURIComponent(operatorQuoteId)}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intent: 'hold', expectedLogId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!json.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

// Task B5 — Approve-on-SEND ("TRS is right; send the quote as-created"). Hits the SAME approve
// endpoint (routes/trs.js → trs-review.approveVerdict); for a SEND/ALREADY_LEGAL verdict the server
// sends via the channel router and returns send_result 'sent'|'send_failed'. A recorded send_failed
// still comes back HTTP-200 with ok:true (the review action completed) — the caller reads
// send_result to pick the pill. Only a true error (HTTP 4xx/5xx, ok:false) throws.
async function postApproveSend(operatorQuoteId) {
  const res = await fetch(`/api/trs/verdicts/${encodeURIComponent(operatorQuoteId)}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
  });
  const json = await res.json().catch(() => ({}));
  if (!json.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;   // { ok:true, send_result, result, ... }
}

// "Approve · don't send" — the verdict was CORRECT but the quote must NOT go out (e.g. the Sentinel
// schedule changed under a still-Pending SEND, so it is no longer valid). Records disposition=approved
// with send_result='not_sent' + closes the quote; NEVER sends. Server: POST /approve-no-send →
// trs-review.approveVerdictNoSend. Optional `note` captures the reviewer's reason (server defaults it).
async function postApproveNoSend(operatorQuoteId, note) {
  const res = await fetch(`/api/trs/verdicts/${encodeURIComponent(operatorQuoteId)}/approve-no-send`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note ? { note } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!json.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;   // { ok:true, result:{ disposition:'approved', send_result:'not_sent' } }
}

// The native send channel a SEND would go out on — derived from the verdict's source when present
// (the GET verdict object may not carry it yet), else fall back to naming the operator's own account.
const SOURCE_CHANNEL_LABEL = {
  centralized_request: 'Sentinel (Centralized Request)',
  avinode: 'Avinode',
  webhook: 'Broker API',
};
function channelLabelFor(v) {
  if (v.source && SOURCE_CHANNEL_LABEL[v.source]) return SOURCE_CHANNEL_LABEL[v.source];
  if (v.source) return v.source;
  return v.operator_id ? `${v.operator_id} (native)` : '—';
}
function fmtPrice(p) {
  if (p == null || p === '') return '—';
  const n = Number(p);
  if (!Number.isFinite(n)) return String(p);
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
// One key/value line in the confirm dialog's summary list.
function DialogRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className={clsx('text-slate-700 text-right', mono && 'font-mono')}>{value == null || value === '' ? '—' : value}</dd>
    </div>
  );
}

// A move can be a string ("leg1->dsn") or an object ({leg, to, ...}) — render compactly either way.
function fmtMoves(moves) {
  if (moves == null) return '—';
  if (typeof moves === 'string') return moves;
  try { return JSON.stringify(moves); } catch (_) { return String(moves); }
}

// Expandable candidate trace — the arrangements the solver tried, with per-candidate legality flags.
function TraceTable({ trace }) {
  if (!Array.isArray(trace) || trace.length === 0) {
    return <div className="text-xs text-slate-400">No candidate trace recorded.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <th className="py-1 pr-3">Candidate</th>
            <th className="py-1 pr-3">Moves</th>
            <th className="py-1 pr-3" title="Read succeeded">ok</th>
            <th className="py-1 pr-3" title="Any RED chip">red</th>
            <th className="py-1 pr-3" title="Any near-limit ORANGE chip">orange</th>
            <th className="py-1 pr-3" title="Minimum crew rest (h) — FAA Part 135 gate ≥10h">minRest</th>
            <th className="py-1 pr-3" title="Clean enough to send">sendable</th>
            <th className="py-1">error</th>
          </tr>
        </thead>
        <tbody>
          {trace.map((c, i) => (
            <tr key={i} className="border-b last:border-0 align-top">
              <td className="py-1 pr-3 text-slate-700">{c.label || `#${i}`}</td>
              <td className="py-1 pr-3 text-slate-500 max-w-xs break-all">{fmtMoves(c.moves)}</td>
              <td className="py-1 pr-3"><BoolCell value={c.ok} trueTone="text-emerald-700" /></td>
              <td className="py-1 pr-3"><BoolCell value={c.anyRed} /></td>
              <td className="py-1 pr-3"><BoolCell value={c.anyOrange} trueTone="text-amber-700" /></td>
              <td className="py-1 pr-3 text-slate-600">{c.minRest ?? '—'}</td>
              <td className="py-1 pr-3"><BoolCell value={c.sendable} trueTone="text-emerald-700" /></td>
              <td className="py-1 text-rose-700 break-all">{c.error || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One verdict card — badge, quote link, reasoning, warnings, action-taken, and an expandable trace.
//
// `onReviewed` — optional callback fired after a successful Decline so the parent can refetch the
// verdict list. Both current callers wire it (TrsVerdicts.jsx re-queries /api/trs/verdicts;
// TripDetail.jsx invalidates the trip-detail query). It defaults to a no-op so any future caller
// that omits it keeps working — the card's own optimistic pill flip (below) keeps the UI correct
// regardless of whether the parent refetches.
export default function VerdictCard({ v, onReviewed = () => {} }) {
  const [showTrace, setShowTrace] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);
  // Task B5 — Approve-&-Send confirm-dialog + in-flight state. `sending` disables every review
  // control the instant Confirm is clicked (the TOCTOU double-send guard — B4 review requirement).
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // Decline form state is LIFTED here (fix 1/3) so only one review action can be in flight at once
  // and stale errors clear on mode switch.
  const [declineSubmitting, setDeclineSubmitting] = useState(false);
  const [declineError, setDeclineError] = useState(null);
  // Optimistic override — set the instant a Decline/Approve POST succeeds, so the pill flips
  // without waiting on the parent's refetch (which may not be wired on every caller).
  const [localDisposition, setLocalDisposition] = useState(null);
  const traceCount = Array.isArray(v.trace) ? v.trace.length : 0;
  const disposition = localDisposition || v.disposition;

  // A SEND-type verdict is the only one that can be Approved-and-SENT (an actual send). HOLD and RECHECK
  // get the confirm-hold button instead; without it a RECHECK had only Decline and sat in Open forever
  // (Yves 2026-10-22, Q#10767). RECHECK is not auto-retried by the worker; ERROR self-retries up to the
  // cap (approving it would not stop the retry/auto-send) and STALE auto-resolves, so those get neither.
  const isSendType = v.verdict === 'SEND' || v.verdict === 'ALREADY_LEGAL';
  const canConfirmHold = canConfirmHoldVerdict(v.verdict);

  // Show the Approve/Decline actions ONLY while the verdict is still awaiting a disposition — once
  // reviewed (this session's optimistic flip OR a prior-session server pill), retire the buttons so
  // a re-click can't write a redundant second review row. 'Verdict-changed' re-opens for re-review
  // (the prior review is stale). 'Send failed' re-opens too so a TRANSIENT send failure can be
  // retried (follow-up fix #4). (Task B2 code-review fix 2.)
  const canReview = !localDisposition &&
    canReviewPill(v.disposition?.pill);   // SQP-297: 'Duplicate — already sent' does NOT re-open
  // Any in-flight review action disables the others (single review action at a time; and the send
  // dialog's Confirm disables-on-click via `sending`).
  const reviewBusy = approving || declineSubmitting || sending;

  function openDecline() {
    setApproveError(null);   // clear a stale Approve error when switching to the Decline form (fix 3)
    setDeclineError(null);
    setDeclineOpen(true);
  }

  function closeDecline() {
    setDeclineError(null);
    setDeclineOpen(false);
  }

  function handleDeclined() {
    setDeclineOpen(false);
    setLocalDisposition({ pill: 'Declined' });
    onReviewed();
  }

  // Task B2 — confirm-held close-out. A lightweight native confirm is enough here: unlike Decline
  // (which captures a structured correction), Approve-HOLD has no fields to fill in — just an
  // "are you sure" gate before the write.
  async function handleApproveHold() {
    if (!window.confirm('Confirm this quote was right to hold? This closes it out — nothing is sent.')) return;
    setApproving(true);
    setApproveError(null);
    setDeclineError(null);   // clear a stale Decline error when switching to Approve (fix 3)
    try {
      await postApproveHold(v.operator_quote_id, v.id);
      setLocalDisposition({ pill: 'Approved (held)' });
      onReviewed();
    } catch (err) {
      setApproveError(err.message || 'Request failed');
    } finally {
      setApproving(false);
    }
  }

  // "Approve · don't send" — the verdict was CORRECT but the quote must NOT go out (e.g. the Sentinel
  // schedule changed under a still-Pending SEND, so it is no longer valid). Records disposition=approved
  // + closes the quote; NEVER sends. Channel-agnostic (a pure DB write), so — unlike Approve & Send —
  // it is offered for a SEND-type verdict on ANY source. Reuses the `approving` in-flight state.
  async function handleApproveNoSend() {
    if (!window.confirm('Approve this verdict as CORRECT but DO NOT send?\n\nUse this when the quote is no longer valid (e.g. the Sentinel schedule changed). The verdict is recorded as correct and the quote is closed — nothing is sent.')) return;
    setApproving(true);
    setApproveError(null);
    setDeclineError(null);
    try {
      await postApproveNoSend(v.operator_quote_id);
      setLocalDisposition({ pill: 'Approved (not sent)' });
      onReviewed();
    } catch (err) {
      setApproveError(err.message || 'Request failed');
    } finally {
      setApproving(false);
    }
  }

  // Task B5 — open the Approve-&-Send confirm dialog. Clears stale errors from the other actions.
  function openSendDialog() {
    setApproveError(null);
    setDeclineError(null);
    setSendError(null);
    setSendDialogOpen(true);
  }
  function closeSendDialog() {
    if (sending) return;   // don't let a mid-send click dismiss the dialog
    setSendError(null);
    setSendDialogOpen(false);
  }

  // Task B5 — Confirm & Send. `sending` gates re-entry (TOCTOU double-send guard). Outcomes:
  //   • send_result 'sent' → close the dialog, flip the pill to 'Approved → Sent', refetch. (This
  //     also covers the reviewWriteFailed edge: the quote WAS sent, so 'Approved → Sent' is honest;
  //     the corpus-reconcile flag is a backend/log concern.)
  //   • send_result 'send_failed' → KEEP the dialog OPEN with the send_error shown inline so the
  //     reviewer sees WHY and can retry, rather than burying it in a pill tooltip (follow-up fix #4).
  //   • a true error (ok:false / HTTP error) → dialog stays open, message shown inline.
  async function handleApproveSend() {
    if (sending) return;
    setSending(true);
    setSendError(null);
    try {
      const json = await postApproveSend(v.operator_quote_id);
      if (json.send_result === 'sent') {
        setSendDialogOpen(false);
        setLocalDisposition({ pill: 'Approved → Sent' });
        onReviewed();
      } else if (json.result && json.result.validationBlocked) {
        // Auto-Validate-before-send blocked it — Sentinel says the quote is no longer valid (e.g. the
        // schedule changed under it). NOTHING was sent. Keep the dialog open, explain, and point the
        // reviewer at "Approve · don't send" to close it out.
        const why = json.result.validation_status === 'error'
          ? "couldn't be validated (transient) — you can retry"
          : `is no longer valid${json.result.reason ? ': ' + json.result.reason : ''}`;
        setSendError(`Not sent — Sentinel says this quote ${why}. Close the dialog and use “Approve · don't send” to resolve it.`);
      } else if (duplicatePillFromSendError(json.result && json.result.send_error)) {
        // Refused by the duplicate-send guard (SQP-297): already delivered — resolved, not a retryable
        // failure. Close the dialog and show the Duplicate pill, as the server will on refetch.
        setSendDialogOpen(false);
        setLocalDisposition({ pill: duplicatePillFromSendError(json.result.send_error) });
        onReviewed();
      } else {
        // send recorded as failed — keep the dialog open, surface the reason, allow a retry.
        setSendError((json.result && json.result.send_error) || 'Send failed — the quote was not sent. You can retry.');
      }
    } catch (err) {
      setSendError(err.message || 'Request failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <VerdictBadge verdict={v.verdict} />
          <DispositionPill disposition={disposition} />
          <QuoteLink qid={v.qid} url={v.sentinel_quote_url} />
          {v.operator_id && <span className="text-xs text-slate-500">· {v.operator_id}</span>}
          {v.trip_id && <span className="text-xs font-mono text-slate-400">· {v.trip_id}</span>}
        </div>
        <div className="text-[11px] text-slate-400 font-mono">{v.created_at}</div>
      </div>

      {/* Reasoning */}
      <div className="mt-2 text-sm text-slate-700">{reasoningFor(v)}</div>

      {/* Base signal + levers — why the solver engaged and what it could move */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className={clsx(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded',
          v.base?.anyRed ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500'
        )}>
          base preview: {v.base?.anyRed ? 'RED chip' : 'no red'}
        </span>
        {Array.isArray(v.base?.levers) && v.base.levers.length > 0 && (
          <span className="inline-flex items-center gap-1 text-slate-500">
            <span className="text-slate-400">levers:</span>
            <span className="font-mono">{v.base.levers.join(', ')}</span>
          </span>
        )}
        {/* SQP-064 surfacing (Yves, 2026-08-10): show the crew DUTY DAY before deciding whether to
            gate on it. VISIBLE, not a tooltip — SQP-061 is the standing lesson that a hover-only
            reason is a reason nobody reads. This is display only; it gates nothing today.
            ⚠️ These are the BASE figures (the schedule as created). The solver always reverts its
            arrangement, so for a shipped quote base duty IS the shipped duty — but on a SEND verdict
            the arrangement that proved legality is NOT what this number describes, hence the label. */}
        {v.duty && v.duty.minutes != null && (
          <span
            className={clsx(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded',
              v.duty.over ? 'bg-rose-50 text-rose-700 font-medium' : 'bg-slate-100 text-slate-500'
            )}
            title={v.duty.over
              ? 'The crew duty day as CREATED exceeds the operator ceiling. Today this does not block the send.'
              : 'Crew duty day as created, within the operator ceiling.'}
          >
            duty {v.duty.hours}h
            {v.duty.over && <span className="ml-0.5">⚠ over ceiling</span>}
            {v.verdict === 'SEND' && <span className="ml-0.5 text-[10px] opacity-70">(as created)</span>}
          </span>
        )}
        {/* Unreadable crew data is a DISTINCT state from "limits exceeded" — SQP-065's requirement.
            Never let the two render the same way. */}
        {v.duty && v.duty.restsUnknown && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">
            crew data unreadable
          </span>
        )}
      </div>

      {/* Crew warnings reacted to */}
      {Array.isArray(v.warnings) && v.warnings.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {v.warnings.map((w, i) => (
            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[11px]">
              {w}
            </span>
          ))}
        </div>
      )}

      {/* Action taken — the SOLVER's own action at solve time, which is always HOLD in shadow.
          ⚠️ CR-1078443 symptom 5 (2026-08-05): this used to read "(shadow — nothing sent)", which is
          a claim about the whole system that a solver-log row cannot support. `shadow`/`action_taken`
          are stamped by cr-crew-solver-log at SOLVE time (shadow defaults true, action_taken defaults
          'HOLD'); the actual send happens later and elsewhere — TRS auto-send, the hourly drain, TRS
          Approve or the dashboard lever — and nothing comes back to update this row. Result: 346 rows
          read verdict=SEND, action_taken=HOLD, shadow=1 on quotes that HAD been delivered, and the
          card asserted "nothing sent" directly underneath a pill correctly reading
          "Auto-resolved — quote sent/approved".
          The true outcome is the DispositionPill above (computeDisposition → terminalAutoReason reads
          the live quote/trip state). This line now says only what the flag actually means, so the two
          can never contradict each other. Correcting the write path is the auto-send lane's call. */}
      <div className="mt-2 text-xs text-slate-500">
        Action taken: <span className="font-medium text-slate-700">{v.action_taken || '—'}</span>
        {v.shadow && <span className="ml-1 text-slate-400">(shadow — the solver itself sent nothing)</span>}
      </div>

      {/* Task B1/B2 — reviewer Approve/Decline. Only offered when we have a stable operator_quote_id
          to key the review row on (Known Issue #55 — never resolve by qid alone) AND the verdict is
          still awaiting a disposition (fix 2 — retire the buttons once reviewed). Approve
          (confirm-held) is HOLD/RECHECK only here — SEND/ALREADY_LEGAL approve-and-send is Task B5. */}
      {v.operator_quote_id && canReview && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          {!declineOpen ? (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Approve & Send — CR channel only for now (follow-up fix #1). For a SEND-type verdict
                  on any other source, show a muted note instead of a button that would 400. */}
              {isSendType && (
                v.source === 'centralized_request' ? (
                  <button
                    type="button"
                    onClick={openSendDialog}
                    disabled={reviewBusy}
                    className={clsx(
                      'text-xs font-semibold hover:underline',
                      reviewBusy ? 'text-slate-400 cursor-not-allowed' : 'text-emerald-700'
                    )}
                  >
                    {sending ? 'Sending…' : 'Approve & Send'}
                  </button>
                ) : (
                  <span className="text-xs text-slate-400 italic">Approve-SEND available on the CR channel only</span>
                )
              )}
              {/* "Approve · don't send" — verdict was right but the quote must not go out (schedule
                  changed / no longer valid). Channel-agnostic close-out; offered for ANY SEND-type
                  verdict (not just CR, unlike Approve & Send). */}
              {isSendType && (
                <button
                  type="button"
                  onClick={handleApproveNoSend}
                  disabled={reviewBusy}
                  title="The verdict was correct, but don't send — e.g. the Sentinel schedule changed and the quote is no longer valid. Records the verdict as correct and closes the quote without sending."
                  className={clsx(
                    'text-xs hover:underline',
                    reviewBusy ? 'text-slate-400 cursor-not-allowed' : 'text-tint'
                  )}
                >
                  {approving ? 'Working…' : "Approve · don't send"}
                </button>
              )}
              {canConfirmHold && (
                <button
                  type="button"
                  onClick={handleApproveHold}
                  disabled={reviewBusy}
                  className={clsx(
                    'text-xs hover:underline',
                    reviewBusy ? 'text-slate-400 cursor-not-allowed' : 'text-tint'
                  )}
                >
                  {approving ? 'Approving…' : 'Approve (confirm hold)'}
                </button>
              )}
              <button
                type="button"
                onClick={openDecline}
                disabled={reviewBusy}
                className={clsx(
                  'text-xs hover:underline',
                  reviewBusy ? 'text-slate-400 cursor-not-allowed' : 'text-rose-700'
                )}
              >
                Decline verdict
              </button>
            </div>
          ) : (
            <DeclineVerdictForm
              operatorQuoteId={v.operator_quote_id}
              onCancel={closeDecline}
              onDeclined={handleDeclined}
              submitting={declineSubmitting}
              setSubmitting={setDeclineSubmitting}
              error={declineError}
              setError={setDeclineError}
            />
          )}
          {approveError && <div className="mt-1 text-xs text-rose-700">{approveError}</div>}
        </div>
      )}

      {/* Expandable trace */}
      {traceCount > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <button
            type="button"
            onClick={() => setShowTrace(s => !s)}
            className="text-xs text-tint hover:underline"
          >
            {showTrace ? '▾ Hide' : '▸ Show'} candidate trace ({traceCount})
          </button>
          {showTrace && <div className="mt-2"><TraceTable trace={v.trace} /></div>}
        </div>
      )}

      {/* Task B5 — Approve & Send confirm dialog (there is no shared Modal component; this is a small
          fixed-overlay panel). Shows the channel + trip/quote/route/tail/price the reviewer is about
          to SEND, an operator crew-swap guidance line when the verdict carried an arrangement (the B6
          hook — harmless when absent), and a clear "this SENDS to the customer as-created" warning. */}
      {sendDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeSendDialog}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-semibold text-slate-800">Approve &amp; Send this quote?</div>
            <dl className="text-xs space-y-1">
              <DialogRow label="Channel" value={channelLabelFor(v)} />
              <DialogRow label="Operator" value={v.operator_id} />
              <DialogRow label="Trip" value={v.trip_id} mono />
              <DialogRow label="Quote" value={v.qid ? `Q#${v.qid}` : null} mono />
              <DialogRow label="Route" value={v.route} mono />
              <DialogRow label="Tail" value={v.tail} mono />
              <DialogRow label="Price" value={fmtPrice(v.price)} />
            </dl>
            {v.arrangement && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                operator crew-swap guidance: <span className="font-mono">{v.arrangement}</span>
              </div>
            )}
            <div className="text-xs text-rose-700 font-medium">
              This SENDS the quote to the customer as-created.
            </div>
            {sendError && <div className="text-xs text-rose-700">{sendError}</div>}
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleApproveSend}
                disabled={sending}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium',
                  sending ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                )}
              >
                {sending ? 'Sending…' : 'Confirm & Send'}
              </button>
              <button
                type="button"
                onClick={closeSendDialog}
                disabled={sending}
                className="px-3 py-1.5 rounded-md text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
